<template>
  <!-- psychologist's video intro: own frame and controls over a YouTube player without YouTube's interface -->
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    content-class="video-intro__dialog"
    overlay-color="#0b2a31"
    overlay-opacity="0.82"
    @input="$emit('input', $event)"
  >
    <div class="video-intro" :class="{ 'video-intro--vertical': ratio < 1 }">
      <div class="video-intro__head">
        <span class="video-intro__title">{{ title }}</span>
        <button type="button" class="video-intro__close" aria-label="Закрити" @click="$emit('input', false)">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
      </div>

      <div ref="frame" class="video-intro__frame" :style="{ aspectRatio: ratioCss }">
        <div ref="holder" class="video-intro__player"></div>

        <!-- covers YouTube's own buttons, title and suggestions: a click plays / pauses -->
        <div v-if="!nativeFallback" class="video-intro__cover" @click="toggle"></div>

        <transition name="video-intro-fade">
          <div v-if="!started || ended" class="video-intro__poster" @click="toggle">
            <img :src="posterUrl || poster" alt="" class="video-intro__poster-img">
            <span class="video-intro__play-big" :class="{ 'video-intro__play-big--loading': starting }">
              <svg v-if="!starting" width="30" height="34" viewBox="0 0 38 44" fill="none"><path d="M36 18.54c2.67 1.54 2.67 5.39 0 6.93L6 42.78C3.33 44.32 0 42.4 0 39.32V4.68C0 1.6 3.33-.32 6 1.22l30 17.32z" fill="#fff"/></svg>
            </span>
          </div>
        </transition>

        <div v-if="started && !ended && !nativeFallback" class="video-intro__controls" @click.stop>
          <button type="button" class="video-intro__btn" :aria-label="playing ? 'Пауза' : 'Відтворити'" @click="toggle">
            <svg v-if="playing" width="18" height="18" viewBox="0 0 18 18"><rect x="3" y="2" width="4" height="14" rx="1" fill="currentColor"/><rect x="11" y="2" width="4" height="14" rx="1" fill="currentColor"/></svg>
            <svg v-else width="18" height="18" viewBox="0 0 18 18"><path d="M4 2.8v12.4c0 .8.9 1.3 1.6.9l10-6.2c.6-.4.6-1.4 0-1.8l-10-6.2C4.9 1.5 4 2 4 2.8z" fill="currentColor"/></svg>
          </button>
          <div
            ref="bar"
            class="video-intro__bar"
            @pointerdown="seekStart"
          >
            <div class="video-intro__bar-fill" :style="{ width: progress * 100 + '%' }"></div>
            <div class="video-intro__bar-knob" :style="{ left: progress * 100 + '%' }"></div>
          </div>
          <span class="video-intro__time">{{ timeText }}</span>
          <button type="button" class="video-intro__btn" :aria-label="muted ? 'Увімкнути звук' : 'Вимкнути звук'" @click="toggleMute">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 7.5h3l4-3.5v12l-4-3.5H3z" fill="currentColor"/><path v-if="!muted" d="M13 7c1.3 1.6 1.3 4.4 0 6M15.3 5c2.4 2.8 2.4 7.2 0 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path v-else d="M13 7.5l5 5M18 7.5l-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          </button>
          <button v-if="canFullscreen" type="button" class="video-intro__btn" aria-label="На весь екран" @click="fullscreen">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 6.5V2h4.5M11.5 2H16v4.5M16 11.5V16h-4.5M6.5 16H2v-4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import youtubeLink from '@/mixins/youtubeLink'

let apiPromise = null

// the YouTube IFrame API is loaded once, on the first opened video
function loadYoutubeApi () {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT)
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous()
        resolve(window.YT)
      }
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(script)
    })
  }
  return apiPromise
}

function clock (seconds) {
  const s = Math.max(0, Math.floor(seconds || 0))
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0')
}

export default {
  name: 'VideoIntroDialog',
  mixins: [youtubeLink],
  props: {
    value: { type: Boolean, default: false },
    link: { type: String, default: '' },
    title: { type: String, default: '' }
  },
  data () {
    return {
      // width / height; shorts are vertical, others are asked from YouTube (oEmbed)
      ratio: 16 / 9,
      posterUrl: '',
      player: null,
      started: false,
      starting: false,
      playing: false,
      ended: false,
      muted: false,
      nativeFallback: false,
      seeking: false,
      current: 0,
      duration: 0,
      timer: null,
      canFullscreen: false
    }
  },
  computed: {
    videoId () {
      return this.getYoutubeId(this.link)
    },
    poster () {
      return this.videoId ? `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg` : ''
    },
    ratioCss () {
      return String(this.ratio)
    },
    maxWidth () {
      if (this.ratio >= 1) return 960
      // vertical video: as tall as the screen allows
      const height = typeof window === 'undefined' ? 700 : Math.min(window.innerHeight * 0.8, 760)
      return Math.round(height * this.ratio) + 24
    },
    progress () {
      return this.duration ? Math.min(1, this.current / this.duration) : 0
    },
    timeText () {
      return clock(this.current) + ' / ' + clock(this.duration)
    }
  },
  watch: {
    value (open) {
      if (open) {
        this.prepare()
      } else {
        this.destroy()
      }
    }
  },
  mounted () {
    const el = document.documentElement
    this.canFullscreen = !!(el.requestFullscreen || el.webkitRequestFullscreen)
    if (this.value) this.prepare()
  },
  beforeDestroy () {
    this.destroy()
  },
  methods: {
    prepare () {
      this.started = false
      this.ended = false
      this.nativeFallback = false
      this.current = 0
      this.duration = 0
      this.ratio = /\/shorts\//.test(this.link || '') ? 9 / 16 : 16 / 9
      this.posterUrl = ''
      if (!this.videoId) return
      const id = this.videoId
      // only vertical videos (shorts) have the tall "oar" thumbnail; for the rest the size comes from YouTube's oEmbed
      const tall = new Image()
      tall.onload = () => {
        if (id !== this.videoId) return
        if (tall.naturalHeight > tall.naturalWidth) {
          this.ratio = 9 / 16
          this.posterUrl = tall.src
        } else {
          this.askRatio(id)
        }
      }
      tall.onerror = () => this.askRatio(id)
      tall.src = `https://i.ytimg.com/vi/${id}/oardefault.jpg`
    },
    askRatio (id) {
      fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent('https://www.youtube.com/watch?v=' + id)}`)
        .then(response => (response.ok ? response.json() : null))
        .then(data => {
          if (id === this.videoId && data && data.width && data.height) this.ratio = data.width / data.height
        })
        .catch(() => {})
    },
    toggle () {
      if (!this.started || this.ended) {
        this.start()
        return
      }
      if (!this.player) return
      if (this.playing) {
        this.player.pauseVideo()
      } else {
        this.player.playVideo()
      }
    },
    start () {
      if (this.player && this.ended) {
        this.ended = false
        this.player.seekTo(0, true)
        this.player.playVideo()
        return
      }
      if (this.starting || !this.videoId) return
      this.starting = true
      loadYoutubeApi().then(YT => {
        if (!this.value || !this.$refs.holder) return
        // YT.Player turns the placeholder into its iframe: a fresh one on every start
        const placeholder = document.createElement('div')
        this.$refs.holder.innerHTML = ''
        this.$refs.holder.appendChild(placeholder)
        this.player = new YT.Player(placeholder, {
          videoId: this.videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0
          },
          events: {
            onReady: event => {
              event.target.playVideo()
              this.duration = event.target.getDuration()
              // a browser that does not let the page start the video: the visitor presses YouTube's own button
              setTimeout(() => {
                if (this.value && !this.playing && !this.started) {
                  this.started = true
                  this.starting = false
                  this.nativeFallback = true
                }
              }, 2500)
            },
            onStateChange: event => this.onState(event.data)
          }
        })
      })
    },
    onState (state) {
      const YT = window.YT
      this.playing = state === YT.PlayerState.PLAYING
      if (this.playing) {
        this.started = true
        this.starting = false
        this.ended = false
        this.duration = this.player.getDuration()
        this.muted = this.player.isMuted()
        this.tick()
      }
      if (state === YT.PlayerState.ENDED) {
        // the poster comes back instead of YouTube's suggestions
        this.ended = true
        this.current = this.duration
      }
    },
    tick () {
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        if (!this.player || !this.player.getCurrentTime) return
        if (!this.seeking) this.current = this.player.getCurrentTime()
        if (!this.playing) clearInterval(this.timer)
      }, 250)
    },
    seekTo (clientX) {
      const rect = this.$refs.bar.getBoundingClientRect()
      const share = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      this.current = share * this.duration
      return this.current
    },
    seekStart (event) {
      if (!this.player || !this.duration) return
      this.seeking = true
      this.seekTo(event.clientX)
      const move = e => this.seekTo(e.clientX)
      const up = e => {
        this.seeking = false
        this.player.seekTo(this.seekTo(e.clientX), true)
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    toggleMute () {
      if (!this.player) return
      if (this.muted) {
        this.player.unMute()
      } else {
        this.player.mute()
      }
      this.muted = !this.muted
    },
    fullscreen () {
      const frame = this.$refs.frame
      const doc = document
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc)
      } else {
        (frame.requestFullscreen || frame.webkitRequestFullscreen).call(frame)
      }
    },
    destroy () {
      clearInterval(this.timer)
      if (this.player && this.player.destroy) this.player.destroy()
      this.player = null
      this.playing = false
      this.starting = false
      this.started = false
      if (this.$refs.holder) this.$refs.holder.innerHTML = ''
    }
  }
}
</script>

<style lang="scss">
.video-intro__dialog {
  box-shadow: none !important;
  overflow: visible !important;
}
</style>

<style lang="scss" scoped>
$teal: #00A3B7;

.video-intro {
  position: relative;
}
.video-intro__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #fff;
}
.video-intro__title {
  font-size: 18px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.video-intro__close {
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  transition: background 0.2s, transform 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.26);
    transform: rotate(90deg);
  }
}
.video-intro__frame {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 20px;
  background: #0b2a31;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08);
  &:fullscreen {
    border-radius: 0;
  }
}
.video-intro--vertical .video-intro__frame {
  max-height: 80vh;
  margin: 0 auto;
}
.video-intro__player,
.video-intro__player ::v-deep iframe,
.video-intro__player ::v-deep > div {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
// the iframe is taller than the frame (the video itself keeps the frame's size, centred):
// YouTube's title, logo and the shorts' like / channel buttons end up outside the frame
.video-intro__player ::v-deep iframe {
  top: -150px;
  height: calc(100% + 300px);
}
.video-intro__cover {
  position: absolute;
  inset: 0;
  cursor: pointer;
}
.video-intro__poster {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #0b2a31;
}
.video-intro__poster-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s;
}
.video-intro__poster:hover .video-intro__poster-img {
  transform: scale(1.03);
}
.video-intro__play-big {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  padding-left: 6px;
  border-radius: 50%;
  background: rgba(0, 163, 183, 0.92);
  box-shadow: 0 0 0 10px rgba(0, 163, 183, 0.25);
  transition: transform 0.25s, box-shadow 0.25s;
  .video-intro__poster:hover & {
    transform: scale(1.08);
    box-shadow: 0 0 0 16px rgba(0, 163, 183, 0.2);
  }
  &--loading::after {
    content: "";
    width: 34px;
    height: 34px;
    margin-left: -6px;
    border: 3px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: video-intro-spin 0.8s linear infinite;
  }
}
@keyframes video-intro-spin {
  to { transform: rotate(360deg); }
}
.video-intro__controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 28px 14px 12px;
  color: #fff;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  opacity: 0;
  transition: opacity 0.25s;
}
.video-intro__frame:hover .video-intro__controls,
.video-intro__controls:focus-within {
  opacity: 1;
}
@media (hover: none) {
  .video-intro__controls {
    opacity: 1;
  }
}
.video-intro__btn {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
}
.video-intro__bar {
  position: relative;
  flex: 1 1 auto;
  height: 18px;
  cursor: pointer;
  touch-action: none;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 7px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);
  }
}
.video-intro__bar-fill {
  position: absolute;
  left: 0;
  top: 7px;
  height: 4px;
  border-radius: 2px;
  background: $teal;
}
.video-intro__bar-knob {
  position: absolute;
  top: 3px;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
.video-intro__time {
  flex: 0 0 auto;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.video-intro-fade-enter-active,
.video-intro-fade-leave-active {
  transition: opacity 0.3s;
}
.video-intro-fade-enter,
.video-intro-fade-leave-to {
  opacity: 0;
}
@media (max-width: 600px) {
  .video-intro__play-big {
    width: 68px;
    height: 68px;
  }
  .video-intro__time {
    display: none;
  }
}
</style>
